import { FC, ReactNode, useMemo } from 'react';

import { Avatar as MuiAvatar, SxProps, useTheme } from '@mui/material';

import {
  ImageResource,
  ImageSizeSpec,
  useResourceImage,
} from '@/shared/lib/hooks/useResourceImage';

interface Props {
  name?: string;
  src?: string;
  sx?: SxProps;
  priority?: boolean;
  userId?: string | number | null;
  currentUser?: boolean;
  size?: ImageSizeSpec;
  children?: ReactNode;
}

const DEFAULT_SIZE: ImageSizeSpec = { tier: 'avatar' };

const Avatar: FC<Props> = ({
  name,
  src,
  sx,
  priority = false,
  userId,
  currentUser = false,
  size = DEFAULT_SIZE,
  children,
}) => {
  const theme = useTheme();

  const resource = useMemo<ImageResource | null>(() => {
    if (currentUser) return { kind: 'current-user-avatar' };
    if (userId != null) return { kind: 'user-avatar', userId: String(userId) };
    return null;
  }, [currentUser, userId]);

  const { url } = useResourceImage(resource, { size });

  const initials =
    name
      ?.split(' ')
      .slice(0, 2)
      .map((w) => w[0])
      .join('') ?? '';

  const usesRemoteFetch = resource != null;
  const effectiveSrc = usesRemoteFetch ? (url ?? undefined) : src;

  return (
    <MuiAvatar
      alt={name}
      src={effectiveSrc}
      imgProps={{
        loading: priority ? 'eager' : 'lazy',
        decoding: 'async',
        ...(priority ? { fetchPriority: 'high' } : { fetchPriority: 'low' }),
      }}
      sx={{ width: '2rem', height: '2rem', backgroundColor: theme.palette.accent.main, ...sx }}
    >
      {children ?? initials}
    </MuiAvatar>
  );
};

export default Avatar;
